import React, { useContext, useRef, useState } from 'react'
import styles from './styles.module.css'
import SyntaxHighlighter from '../../../components/SyntaxHighlighter'
import { FaPlay, FaPause } from 'react-icons/fa'
import { areaCustomGradient, data, pointerObject } from './data'
import ProTip from '../../../components/ProTip'
import { AppContext } from '../../../AppContext'
import { appConstants, domain, lineStyle } from '../../../constants'
import { headers as lineHeaders } from '../LineChart/body'

export const headers = {
  minimal: { id: 'minimal', label: 'Minimal chart' },
  styling: { id: 'styling', label: 'Styling' },
  curved: { id: 'curved', label: 'Curved' },
  customGrad: { id: 'customGrad', label: 'Custom Gradient' },
  multiple: { id: 'multiple', label: 'Multiple Lines' },
  animated: { id: 'animated', label: 'Animated' },
  customDataPoints: { id: 'customDataPoints', label: 'Custom Data Points' },
  pointerConfig: { id: 'pointerConfig', label: 'pointerConfig' },
  animateOnChange: { id: 'animateOnChange', label: 'Animate On Data Change' }
}

export const MainBody = (props) => {
  const { isMobile, onBodyScroll } = props

  const appContext = useContext(AppContext)
  const { width, theme } = appContext
  const { sideDrawerWidth, rightNavWidth } = appConstants

  const isSideDrawerVisible = width > 1100

  const mainBodyWidth = isMobile
    ? width
    : width -
      rightNavWidth -
      (isSideDrawerVisible ? sideDrawerWidth - 10 : 30)

  const [isPlaying, setIsplaying] = useState(false)
  const [isPlaying2, setIsplaying2] = useState(false)
  const videoRef = useRef()
  const onplayClick = () => {
    if (isPlaying) {
      videoRef.current.pause()
      setIsplaying(false)
    } else {
      videoRef.current.play()
      setIsplaying(true)
    }
  }
  const onVideoEnd = () => {
    setIsplaying(false)
  }

  const videoRef2 = useRef()
  const onplayClick2 = () => {
    if (isPlaying2) {
      videoRef2.current.pause()
      setIsplaying2(false)
    } else {
      videoRef2.current.play()
      setIsplaying2(true)
    }
  }

  const onVideoEnd2 = () => {
    setIsplaying2(false)
  }

  return (
    <div
      className={styles.container}
      style={{ paddingLeft: isMobile ? 3 : 20, width: mainBodyWidth }}
      onScroll={onBodyScroll}
    >
      <h2>
        Area Charts
        <a
          style={{ marginLeft: 20, fontSize: 15, color: theme==='dark'? 'skyblue' : 'blue' }}
          target='_blank'
          href='https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/blob/master/docs/LineChart/LineChartProps.md'
          rel='noreferrer'
        >
          props table
        </a>
      </h2>
      <ProTip
        body={() => {
          return (
            <>
              You can convert any line chart into area chart by just passing the{' '}
              <b>areaChart</b> prop to the {'<LineChart/>'} component.
            </>
          )
        }}
      />
      <div style={{ marginLeft: 10, marginBottom: 100 }}>
        <h4 id={headers.minimal.id}>Minimal Area Chart</h4>
        <div style={{ marginLeft: 10 }}>
          The simplest Area chart can be made with just-
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[0].code}
          </SyntaxHighlighter>
          <h5>Output-</h5>
          <img src={data[0].imgSrc} className={styles.barOutput} alt='' />
        </div>

        <h4 id={headers.styling.id}>Styling the Chart</h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            Area charts look the best with gradient fill. And for this reason,
            all area charts are given with a gradient by degault. The default
            gradient colors are gray and white.
          </p>
          <p>
            So we will start styling the Area chart by changing its gradient
            color and opacity. For this, we use the following props-
          </p>
          <ul>
            <li>startFillColor</li>
            <li>startOpacity</li>
            <li>endFillColor</li>
            <li>endOpacity</li>
            <li>gradientDirection</li>
            <li>areaGradientId</li>
            <li>areaGradientComponent</li>
          </ul>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[1].code}
          </SyntaxHighlighter>
          <h5>Output-</h5>
          <img src={data[1].imgSrc} className={styles.barOutput} alt='' />
        </div>

        <hr />
        <br />

        <h4 id={headers.curved.id}>Curved Area Chart</h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            Using a combination of the props- <b>areaChart</b> and <b>curved</b>{' '}
            results in a curved Area chart. The above area chart after adding
            the <i>curved</i> prop-
          </p>
          <img
            src={require('../../../assets/curvedArea.png')}
            className={styles.barOutput}
            alt=''
          />
        </div>

        <br />

        <h4 id={headers.customGrad.id}>Custom Gradient component</h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            Area fill colors and gradient direction can be controlled using the{' '}
            <b>startFillColor</b>, <b>endFillColor</b> and{' '}
            <b>gradientDirection</b> props.
            <br />
            <br />
            Sometimes, we want more than 2 colors for the gradient, or we want a
            direction other than <i>vertical</i> or <i>horizontal</i>{' '}
            {`(e.g. slanting)`}.<br />
            In such cases, we can use our own custom gradient component using
            the <b>areaGradientComponent</b> and <b>areaGradientId</b> props.
          </p>
          <p>Here's an example where we need a custom gradient component-</p>
          <img
            src={require('../../../assets/custGrad.png')}
            className={styles.outputBig}
            alt=''
          />
          <p>The code for the above chart is-</p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {areaCustomGradient}
          </SyntaxHighlighter>
          <p>
            Note that <b>areaGradientId</b> prop is mandatory when using custom
            gradient component, and it should have the same value as the id
            passed in {`<LinearGradient>`}.
          </p>
        </div>

        <h4>Labelled Area Chart</h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            Adding the property <b>label</b> in the data array displays labels
            beside the data points.
          </p>
        </div>

        <hr />
        <br />
        <h4 id={headers.multiple.id}>Multiple Lines</h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            Area charts are often used to compare 2 or more data sets. Below is
            an example -
          </p>
          <img src={data[2].imgSrc} className={styles.outputBig} alt='' />
          <p>
            For such type of charts, we pass 2 data sets named <b>data</b> and{' '}
            <b>data2</b>
            <br />
            Here is the code for the above chart -
          </p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[2].code}
          </SyntaxHighlighter>
        </div>

        <hr />
        <br />
        <h4 id={headers.animated.id}>Animated Area Chart</h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            Adding the prop <b>isAnimated</b> to any chart renders the chart
            with an animation effect. here's an animated Area chart-
          </p>

          <video
            ref={videoRef}
            muted
            autoPlay
            onEnded={onVideoEnd}
            className={styles.outputBig}
            src={require('../../../assets/areaanim.mov')}
          />

          <p style={{ fontSize: 24 }}>
            {isPlaying ? (
              <FaPause onClick={onplayClick} />
            ) : (
              <FaPlay onClick={onplayClick} />
            )}
          </p>

          <p>The code for the above chart is-</p>

          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[3].code}
          </SyntaxHighlighter>
        </div>

        <br />
        <h4 id={headers.customDataPoints.id}>
          Custom Data Points and Vertical strip
        </h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            Normally, the data points can be circular (default) or rectangular
            in shape. Their size and color can be controlled through props.
            <br />
            Adding the <b>customDataPoint</b> prop allows you to use your own
            components as the data points. Similarly, adding the{' '}
            <b>dataPointLabelComponent</b> prop allows you to use your own
            components as the data point labels.
          </p>

          <p>Data point label component related props-</p>

          <ul>
            <li>
              <b>dataPointLabelComponent</b>
            </li>
            <li>
              <b>dataPointsHeight</b>
            </li>
            <li>
              <b>dataPointsWidth</b>
            </li>
            <li>
              <b>dataPointLabelShiftY</b>
            </li>
            <li>
              <b>dataPointLabelShiftX</b>
            </li>
          </ul>
          <p>Strip related props-</p>

          <ul>
            <li>
              <b>showStrip</b>
            </li>
            <li>
              <b>stripHeight</b>
            </li>
            <li>
              <b>stripWidth</b>
            </li>
            <li>
              <b>stripColor</b>
            </li>
            <li>
              <b>stripOpacity</b>
            </li>
          </ul>

          <p>
            <span className={styles.noteText} id='activatePointersOnLongPress'>
              Note:
            </span>
            When using <b>customdataPoint</b>, make sure to pass the custom
            point's height and using the props- <b>dataPointsHeight</b> and{' '}
            <b>dataPointsWidth</b>. Not passing these props might result in an
            improper alignment of the custom data points.
          </p>

          <img src={data[4].imgSrc} className={styles.outputBig} alt='' />
          <br />

          <p>The code for the above chart is-</p>

          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[4].code}
          </SyntaxHighlighter>
        </div>
        <hr />

        <br />
        <h4>Enabling press, and focusing the respective data point</h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            Many Area/Line charts, specially those in trading apps, allow users
            to press on any part of the chart, and highlight the respective data
            point.
            <br />
            Implementing this has been described in the{' '}
            <a style={{color: theme==='dark'? 'skyblue' : 'blue'}} href={`${domain}linechart/#${lineHeaders.focus.id}`}>
              <b>Line chart</b> in the <b>focus</b> section.
            </a>
          </p>
        </div>

        <hr />

        <br />

        <h4 id={headers.pointerConfig.id}>pointerConfig</h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            pointerConfig is an object, when passed as a prop, creates a{' '}
            <span style={{ color: 'green', fontWeight: 'bold' }}>magical</span>{' '}
            effect. It lets the user scroll over chart to move the pointer along
            the chart.
            <br />
            For the sake of a very simple example, let's try adding the{' '}
            <b>pointerConfig</b> prop to our first example. The pointerConfig
            prop should be an object. For now, let's use an empty object. So,
            the code will be-
          </p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[0].code.replace(
              'data={data}',
              'data={data} pointerConfig={{}}'
            )}
          </SyntaxHighlighter>
          <p>This results in the below chart!</p>
          <img
            src={require('../../../assets/pointer.gif')}
            style={{ height: 280 }}
            alt=''
          />

          <p>
            So, this was the simplest example with the{' '}
            <span style={{ color: 'green', fontWeight: 'bold' }}>magical</span>{' '}
            scroll effect.
          </p>
          <h4>Understanding the pointerConfig</h4>
          <p>The pointerConfig object can have following fields-</p>

          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {pointerObject}
          </SyntaxHighlighter>
          <p>
            The above properties can be understood with this labelled diagram-
          </p>
          <img
            src='https://github.com/Abhinandan-Kushwaha/react-native-gifted-charts/raw/master/demos/rainbow.png'
            // style={{height:280}}
            alt=''
          />
          <p>
            <span className={styles.noteText} id='activatePointersOnLongPress'>
              Note:
            </span>
            If you are using the <b>pointerConfig</b> prop, the{' '}
            <span style={{ color: 'brown' }}>{`scroll will be disabled `}</span>
            automatically. This is because, it's difficult to achive both
            scrolling line and scrolling pointer simultaneously. So if you want
            to retain the scroll behaviour even after passing the pointerConfig
            prop, then set the property{' '}
            <b style={{ color: theme==='dark'? 'skyblue' : 'blue' }}>activatePointersOnLongPress</b> to true
            inside the pointerConfig object. This will make the pointers visible
            only after long press. <br />
            So, before the long press, user can scroll the line. Once long
            pressed, scrolling will be disabled until the release of the long
            press. The duration after which a press event will be considered as
            long press can be controlled using the
            <b>{` activatePointersDelay`}</b> property inside the pointerConfig
            object. The default value of activatePointersDelay is 150.
          </p>
          <p>
            Here's a beautiful example of an area chart having the magical
            scroll effect using the <b>pointerConfig</b> prop-
          </p>

          <video
            ref={videoRef2}
            muted
            autoPlay
            onEnded={onVideoEnd2}
            className={styles.outputBig}
            src={require('../../../assets/pointerConfig.mp4')}
          />

          <p style={{ fontSize: 24 }}>
            {isPlaying2 ? (
              <FaPause onClick={onplayClick2} />
            ) : (
              <FaPlay onClick={onplayClick2} />
            )}
          </p>

          <p>The code for the above chart is-</p>

          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[6].code}
          </SyntaxHighlighter>
        </div>

        <h4>pointerLabelComponent</h4>
        <div style={{ marginLeft: 10 }}>
          <b>pointerLabelComponent</b> is a function that returns the component
          to be rendered as a Label. It takes 3 parameters -
          <ol>
            <li>an array of items</li>
            <li>secondaryDataItem</li>
            <li>pointerIndex.</li>
          </ol>
          So, if there are multiple data arrays, the parameter <b>items</b> will
          have the data item corresponding to each data array. If the chart has
          secondaryData, the parameter <b>secondaryDataItem</b> will have the
          corresponding item from the secondaryData.
          <span className={styles.noteText}>Note:</span> If you have a Pressable
          / Touchable item in your <b>pointerLabelComponent</b>, then you should
          set <b>pointerEvents</b> to <i>'auto'</i> inside the pointerConfig
          object.
          <h4>getPointerProps</h4>
          <b>getPointerProps</b> prop can be used to get the current pointer's
          index, x and y coordinate values. It is a callback function that
          accepts a single parameter which is an object. This object has
          following properties-
          <ol>
            <li>pointerIndex</li>
            <li>pointerX</li>
            <li>pointerY</li>
          </ol>
          When the chart is pressed, it returns the index of the data point
          pressed.
          <br />
          When the chart is scrolled after pressing, it returns the index of the
          data point currently focused.
          <br />
          When the chart is released, it returns the index -1.
          <br />
          <h4>pointerColorsForDataSet</h4>
          When using pointers with dataSet, you can set pointer colors on each
          data line using the pointerColorsForDataSet which is an array of color
          values.
          <h4>dynamicLegendComponent</h4>
          <b>dynamicLegendComponent</b> is a property inside the{' '}
          <b>pointerConfig</b> prop, very similar to{' '}
          <i>pointerLabelComponent</i>, the only difference is that it is
          stationary whereas <i>pointerLabelComponent</i> moves as the pointer
          moves. You can set the position of the <i>dynamicLegendComponent</i>{' '}
          using the
          <b>
            <i>dynamicLegendContainerStyle</i>
          </b>{' '}
          property in the <i>pointerConfig</i>
          <br /> <br />
          You are supposed to assign a callback function to
          <i>dynamicLegendComponent</i>. The callback function receives 2
          parameters-
          <ol>
            <li>
              Array of currently selected items (in case you are rendering a
              single line, the array will have a single item)
            </li>
            <li>Index of the selected item.</li>
          </ol>
          <br />
          <span className={styles.noteText}>Note:</span> The legend component
          appears only as long as the pointer remains remains on the screen. To
          make the dynamic legend remain persistently on the screen, you can set
          the <b>persistPointer</b> property to true. The{' '}
          <i>initialPointerIndex</i>
          property can also be useful.
        </div>

        <div style={{ marginLeft: 10 }}>
          <p>Here's another beautiful Area chart with pointer</p>
          <img
            src='https://user-images.githubusercontent.com/20596944/166833722-00d8213f-3d7d-422b-840d-c5fe92b3bfb9.gif'
            style={{ height: 280 }}
            alt=''
          />
          <p>The code for the above chart is-</p>
          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[7].code}
          </SyntaxHighlighter>
        </div>
        <hr />

        <br />
        <h4 id={headers.animateOnChange.id}>Animate on data change</h4>
        <div style={{ marginLeft: 10 }}>
          <p>
            To make your data representation outstanding, you can consider
            adding <b>smooth transitions</b> to your chart on <i>data change</i>
            .
            <br />
            <br />
            Here's an example-
          </p>
          <img src={data[5].imgSrc} style={{ height: 280 }} alt='' />
          <p>
            Surprisingly, this huge feat can be achieved just by adding a prop
            called <b>animateOnDataChange</b>. And the duration of the
            transition animation can be controlled using the{' '}
            <b>onDataChangeAnimationDuration</b> prop.
            <br />
            <br />
          </p>

          <p>
            <span className={styles.noteText}>Note:</span> Currently, this
            transition animation is{' '}
            <b>
              not supported for <i>curved</i>
            </b>{' '}
            Line or Area charts. Also, in case of multiple data lines, the
            transition animation will only be applicable to the first data line.
          </p>

          <SyntaxHighlighter
            showLineNumbers
            lineNumberStyle={lineStyle}
            language='javascript'
          >
            {data[5].code}
          </SyntaxHighlighter>
          <p>
            Now, whenever the values in the data <i>(currentData)</i> change,
            you can see it transition smoothly.
            <br />
            <br />
            To test it, you can change the values in the data on some event,
            like on press of a button.
          </p>
        </div>
        <hr />
        <br />
      </div>
    </div>
  )
}
